/**
 * UserManagerSettings for the local ZITADEL instance. Read once at module
 * load (env vars are baked into the bundle by Vite, no runtime mutation).
 *
 * Redirect URIs are computed from `window.location.origin` rather than
 * pulled from env so dev / preview / prod all "just work" without per-
 * environment config. The trade-off: the same exact URI must be registered
 * in the ZITADEL Console (Applications → XAES Dashboard → URLs). For local
 * dev that's `http://localhost:5173/auth/callback` and the post-logout
 * `http://localhost:5173/`.
 */

import type { UserManagerSettings } from "oidc-client-ts";
import { WebStorageStateStore } from "oidc-client-ts";
import { env } from "@/env";

export function buildOidcSettings(): UserManagerSettings {
  const origin = window.location.origin;
  return {
    authority: env.VITE_ZITADEL_ISSUER,
    client_id: env.VITE_ZITADEL_CLIENT_ID,
    redirect_uri: `${origin}/auth/callback`,
    post_logout_redirect_uri: `${origin}/`,
    response_type: "code",
    // openid + profile + email = identity basics.
    // urn:zitadel:iam:org:project:roles = ZITADEL project-role claim;
    // gated by "User roles inside ID Token" / "Add user roles to access
    // token" in the Console (Application → Token Settings).
    // urn:zitadel:iam:org:project:id:zitadel:aud = adds the internal
    // "zitadel" project's audience to the access token, which the
    // Management API at /management/v1/* requires. Without it any
    // mgmt-API call returns AUTH-7fs1e Errors.Token.Invalid even with
    // a valid access token, because Zitadel checks audience first.
    // urn:zitadel:iam:user:resourceowner = the user's RESIDENT org claims
    // (id/name/primary_domain) — the multi-client identity anchor. The BFF
    // reads the same claim off the access token to resolve the caller's
    // client; the SPA uses it only for cache keying + client display.
    scope:
      "openid profile email urn:zitadel:iam:org:project:roles urn:zitadel:iam:org:project:id:zitadel:aud urn:zitadel:iam:user:resourceowner",
    // sessionStorage matches the POC token-storage decision in
    // infra/zitadel/PLAN.md step 10. Production hardening (BFF +
    // http-only cookies) is Phase 8 work.
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    // Silent renew on a hidden iframe — keeps the access token fresh
    // without bouncing the user through the Login UI again.
    automaticSilentRenew: true,
    // Refresh tokens are issued by ZITADEL when the client is configured
    // with `offline_access` scope; we don't request that yet to keep the
    // PoC simple. Silent renew via prompt=none + iframe is enough for now.
    loadUserInfo: true,
  };
}
